import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { type ComponentProps, useContext } from 'react'
import { cn } from '../lib/cn'
import { type ControlSize, type ControlVariant, controlClassName } from '../lib/control-styles'
import { FieldContext, useFieldControlProps } from '../lib/field-context'

type SelectProps = ComponentProps<typeof SelectPrimitive.Root> &
  Omit<
    ComponentProps<typeof SelectPrimitive.Trigger>,
    keyof ComponentProps<typeof SelectPrimitive.Root> | 'asChild' | 'onChange'
  > & {
    variant?: ControlVariant
    controlSize?: ControlSize
    placeholder?: ComponentProps<typeof SelectPrimitive.Value>['placeholder']
  }

export function Select({
  children,
  className,
  variant = 'outline',
  controlSize = 'md',
  placeholder,
  value,
  defaultValue,
  onValueChange,
  open,
  defaultOpen,
  onOpenChange,
  name,
  autoComplete,
  disabled,
  required,
  form,
  dir,
  ...props
}: SelectProps) {
  const field = useFieldControlProps(props)
  const fieldContext = useContext(FieldContext)
  return (
    <SelectPrimitive.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      name={name}
      autoComplete={autoComplete}
      disabled={disabled}
      required={required}
      form={form}
      dir={dir}
    >
      <SelectPrimitive.Trigger
        {...props}
        {...field}
        data-slot="select"
        className={controlClassName(
          variant,
          cn(
            'flex items-center justify-between gap-2 text-start data-[placeholder]:text-muted-foreground [&>span:first-child]:truncate',
            className,
          ),
          controlSize,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          data-slot="select-content"
          aria-label={props['aria-label']}
          aria-labelledby={
            props['aria-labelledby'] ?? (props['aria-label'] ? undefined : fieldContext?.labelId)
          }
          position="popper"
          sideOffset={8}
          collisionPadding={12}
          className={cn(
            'z-[60] max-h-[min(20rem,var(--radix-select-content-available-height))] w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-content-available-width)] overflow-hidden rounded-2xl border border-border bg-card text-foreground shadow-[0_14px_36px_rgba(43,35,32,0.14)] outline-none',
            'origin-[var(--radix-select-content-transform-origin)] motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1 text-muted-foreground">
            <ChevronUp aria-hidden="true" className="size-4" />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="p-1.5">{children}</SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1 text-muted-foreground">
            <ChevronDown aria-hidden="true" className="size-4" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

export function SelectItem({
  children,
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      {...props}
      data-slot="select-item"
      className={cn(
        'relative flex min-h-10 cursor-default select-none items-center rounded-xl py-2 pe-9 ps-3 text-sm outline-none transition-colors [&>span:first-child]:min-w-0 [&>span:first-child]:break-words',
        'data-[state=checked]:font-medium data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-55',
        className,
      )}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute end-3 flex items-center text-action">
        <Check aria-hidden="true" className="size-4" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}
